import React, { useCallback, useState } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AutomationForm } from '@traquility/ui/diary';
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from '../ui/common/Dropdown';
import type {
  AutomationFormData,
  AutomationFormLabels,
  AutomationFormContainerProps,
} from '../shared/types/automationForm';

export type { AutomationFormContainerProps };

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 10,
    },
  },
});

const CREATE_AUTOMATION_MUTATION = `
  mutation CreateAutomation($input: CreateAutomationInput!) {
    createAutomation(input: $input) {
      automation {
        id
        fieldId
        periodName
      }
      errors
    }
  }
`;

const UPDATE_AUTOMATION_MUTATION = `
  mutation UpdateAutomation($input: UpdateAutomationInput!) {
    updateAutomation(input: $input) {
      automation {
        id
        fieldId
        periodName
      }
      errors
    }
  }
`;

interface DeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDelete: () => void;
  labels: AutomationFormLabels;
  isDeleting: boolean;
}

function DeleteModal({ isOpen, onClose, onDelete, labels: l, isDeleting }: DeleteModalProps) {
  if (!isOpen) return null;

  return (
    <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{l.deleteTitle}</h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={isDeleting}></button>
          </div>
          <div className="modal-body">
            <p>{l.deleteConfirmation}</p>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isDeleting}>
              {l.cancel}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={onDelete}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  {l.deleteButton}
                </>
              ) : (
                l.deleteButton
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AutomationFormContainerInner({
  graphqlEndpoint,
  csrfToken,
  categoryId,
  initialData,
  backUrl,
  successUrl,
  labels,
  errors: initialErrors = {},
  mode,
  periodOptions,
  periodUnitOptions,
  fieldGroups,
}: AutomationFormContainerProps) {
  const [errors, setErrors] = useState<Record<string, string[]>>(initialErrors);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSubmit = useCallback(async (data: AutomationFormData) => {
    setIsSubmitting(true);
    setErrors({});

    const isEdit = mode === 'edit';
    const mutation = isEdit ? UPDATE_AUTOMATION_MUTATION : CREATE_AUTOMATION_MUTATION;

    const input: Record<string, unknown> = {
      fieldId: data.fieldId,
      periodName: data.periodName,
      onMonday: data.onMonday,
      onTuesday: data.onTuesday,
      onWednesday: data.onWednesday,
      onThursday: data.onThursday,
      onFriday: data.onFriday,
      onSaturday: data.onSaturday,
      onSunday: data.onSunday,
    };

    if (isEdit) {
      input.id = data.id;
    } else {
      input.categoryId = categoryId;
    }

    if (data.periodName === 'custom') {
      input.periodUnit = data.periodUnit;
      input.periodStep = data.periodStep;
      if (data.startDate) {
        input.startDate = data.startDate;
      }
    }

    try {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ query: mutation, variables: { input } }),
      });

      if (!response.ok) throw new Error('Network error');

      const result = await response.json();

      if (result.errors) {
        setErrors({ base: result.errors.map((e: { message: string }) => e.message) });
        setIsSubmitting(false);
        return;
      }

      const mutationResult = isEdit ? result.data.updateAutomation : result.data.createAutomation;

      if (mutationResult.errors?.length > 0) {
        setErrors({ base: mutationResult.errors });
        setIsSubmitting(false);
        return;
      }

      window.location.href = successUrl;
    } catch {
      setErrors({ base: ['An unexpected error occurred. Please try again.'] });
      setIsSubmitting(false);
    }
  }, [graphqlEndpoint, csrfToken, categoryId, successUrl, mode]);

  const handleCancel = useCallback(() => {
    window.location.href = backUrl;
  }, [backUrl]);

  const handleDelete = useCallback(async () => {
    setIsDeleting(true);

    try {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: `
            mutation DeleteAutomation($input: DeleteAutomationInput!) {
              deleteAutomation(input: $input) {
                success
                errors
              }
            }
          `,
          variables: { input: { id: initialData.id } },
        }),
      });

      const result = await response.json();

      if (result.errors || result.data.deleteAutomation.errors?.length > 0) {
        const msgs = result.errors?.map((e: { message: string }) => e.message) ||
          result.data.deleteAutomation.errors;
        setErrors({ base: msgs });
        setIsDeleting(false);
        setShowDeleteModal(false);
        return;
      }

      window.location.href = backUrl;
    } catch {
      setErrors({ base: ['An unexpected error occurred. Please try again.'] });
      setIsDeleting(false);
    }
  }, [graphqlEndpoint, csrfToken, initialData.id, backUrl]);

  return (
    <>
      {mode === 'edit' && (
        <div className="position-absolute" style={{ top: 10, right: 10 }}>
          <Dropdown>
            <DropdownTrigger className="btn btn-link text-secondary">
              <i className="bi bi-three-dots"></i>
            </DropdownTrigger>
            <DropdownMenu align="end">
              <DropdownItem className="text-danger" onClick={() => setShowDeleteModal(true)}>
                {labels.delete}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
      )}

      <AutomationForm
        initialData={initialData}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        errors={errors}
        labels={labels}
        mode={mode}
        periodOptions={periodOptions}
        periodUnitOptions={periodUnitOptions}
        fieldGroups={fieldGroups}
        isSubmitting={isSubmitting}
      />

      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={handleDelete}
        labels={labels}
        isDeleting={isDeleting}
      />
    </>
  );
}

function AutomationFormContainer(props: AutomationFormContainerProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <AutomationFormContainerInner {...props} />
    </QueryClientProvider>
  );
}

let formRoot: Root | null = null;

export function mountAutomationForm(
  container: HTMLElement,
  props: AutomationFormContainerProps
): Root {
  if (formRoot) formRoot.unmount();
  const root = createRoot(container);
  formRoot = root;
  root.render(<AutomationFormContainer {...props} />);
  return root;
}

export function unmountAutomationForm(): void {
  if (formRoot) {
    formRoot.unmount();
    formRoot = null;
  }
}

export default AutomationFormContainer;
